import React, { type ElementType } from 'react';
import { LabelIconRow } from '@features/appointment-labels';
import { formatTimeRange } from '@features/appointments/utils/dateTimeFormatters';
import { EligibilityBadge } from '@features/eligibility-checks';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import { Box, type SvgIconProps, type SxProps } from '@mui/material';

import type { CalendarEvent } from '../../../types';

import {
  ConfirmedIcon,
  EventWrapper,
  Icon,
  TimeLabel,
  TitleRow,
  TitleText,
  TitleWrapper,
} from './BaseEvent.styles';

/**
 * Props for BaseEvent component
 */
export interface BaseEventProps {
  event: CalendarEvent;
  title: React.ReactNode;
  viewMode?: string;
  icon?: ElementType<SvgIconProps>;
  isHovered?: boolean;
  eventRef?: React.Ref<HTMLDivElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  sx?: SxProps;
}

/**
 * BaseEvent - Unified event component that consumes CSS variables for variant styling
 *
 * @example
 * const cssVars = createEventCSSVars('actionable', colorTreatment, 'dashed', isHovered);
 * <BaseEvent event={event} title={title} icon={CheckIcon} sx={cssVars} />
 */
export const BaseEvent: React.FC<BaseEventProps> = ({
  event,
  title,
  viewMode,
  icon,
  eventRef,
  onMouseEnter,
  onMouseLeave,
  onClick,
  sx,
}) => {
  return (
    <EventWrapper
      ref={eventRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      sx={sx}
    >
      {icon && <Icon component={icon} />}
      <TitleWrapper>
        <TitleRow>
          <TitleText>{title}</TitleText>
          {viewMode !== 'month' && <LabelIconRow labels={event.resource.labels ?? []} />}
          {event.resource.notes && <StickyNote2Outlined sx={{ fontSize: 14, opacity: 0.7 }} />}
          <EligibilityBadge
            patientId={event.resource.patient?.patient_id}
            encounterId={
              (event.resource.encounter as { encounter_id?: string } | null)?.encounter_id ??
              undefined
            }
            source="calendar_block"
            showActionLabel={false}
            disablePopover
          />
          {event.resource.confirmation_status === 'confirmed' && <ConfirmedIcon />}
        </TitleRow>

        {/* Time label - hidden in month view to reduce event height */}
        {viewMode !== 'month' && <TimeLabel>{formatTimeRange(event.start, event.end)}</TimeLabel>}
        {viewMode !== 'month' && event.resource.no_showed === true && <Box>No Show</Box>}
      </TitleWrapper>
    </EventWrapper>
  );
};
